/**
 * T-144 / DP: the unread "new documents" count for the Downloads nav badge.
 * Signed-in client (external) users only. Polls the cheap portal count endpoint
 * on a gentle cadence and refetches on navigation — so opening /portal (which
 * marks documents read) clears the badge shortly after — without a polling
 * storm. Never throws: getPortalDocUnreadCount returns 0 on any failure.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPortalDocUnreadCount, PORTAL_DOCS_READ_EVENT } from '@/lib/portalApi';

const POLL_MS = 45_000;

export function useDownloadsUnreadCount(enabled: boolean): number {
  const [count, setCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    // When disabled we simply don't fetch; the return value below yields 0, so a
    // stale count is never shown (and we avoid a setState directly in the effect).
    if (!enabled) return undefined;
    let cancelled = false;
    const load = () => getPortalDocUnreadCount('account').then((n) => { if (!cancelled) setCount(n); });
    load();
    const id = window.setInterval(load, POLL_MS);
    // Refetch immediately when the documents view marks things read, so the badge
    // clears without waiting for the next poll.
    window.addEventListener(PORTAL_DOCS_READ_EVENT, load);
    return () => { cancelled = true; window.clearInterval(id); window.removeEventListener(PORTAL_DOCS_READ_EVENT, load); };
    // location.pathname is a dep so a navigation also refetches promptly.
  }, [enabled, location.pathname]);

  return enabled ? count : 0;
}
